'use client'

import { MixpanelOobTokenFragment, MixpanelTokenFragment, ProjectDisplayFragment } from '@/gql/generated/graphql'
import { AllPurposeLink } from '@components/all-purpose-link'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { getInvocationFromTokenId, SoloViewerToken } from '@utils/common'
import { marketplaceTokenRoute, userProfileRoute } from '@utils/routes'
import { AttributeField, sortFeatures } from '@utils/token-features'
import { BodyText, DetailText, HeadlineText } from 'design-configuration/components'
import { ImageIcon, ArrowSquareOutIcon, FullScreenIcon, CloseDrawerIcon } from 'design-configuration/components/icons'
import { motion } from 'motion/react'
import React from 'react'
import { isOob } from '.'
import { Button, buttonVariants } from '@components/button'
import { cn } from '@/lib/utils'
import { useUserDisplay } from '@/hooks/use-user-display'

type DisplayNotesProps = {
  project: ProjectDisplayFragment
  token: SoloViewerToken
  liveViewActive: boolean
  entryDirection: 'left' | 'bottom'
  onClose: () => void
  onFullScreenClick: () => void
  className?: string
}

enum DisplayNotesView {
  Notes = 'Display notes',
  Features = 'Features'
}

export const DisplayNotes = ({
  token,
  project,
  className,
  entryDirection,
  onClose,
  onFullScreenClick
}: DisplayNotesProps): React.ReactElement => {
  const isOobToken = isOob(token)
  const { displayName: ownerDisplayName } = useUserDisplay({
    name: isOobToken ? undefined : token.owner?.profile_by_id?.name,
    username: isOobToken ? undefined : token.owner?.profile_by_id?.username,
    address: isOobToken ? undefined : token.owner_address
  })

  const tokenFeatures: Record<string, number | string> = token && 'features' in token ? token.features : {}
  const featureFields: AttributeField[] = project?.features?.feature_fields
  let features = Object.entries(tokenFeatures)
  if (features && featureFields) {
    const featuresOrderList = featureFields.map((field) => {
      return field.name
    })

    features = features.slice().sort((a, b) => {
      return sortFeatures(featuresOrderList, a, b)
    })
  }

  const [notesView, setNotesView] = React.useState<DisplayNotesView>(
    project.artist_display_notes ? DisplayNotesView.Notes : DisplayNotesView.Features
  )

  const changeTabs = (tab: DisplayNotesView) => {
    setNotesView(tab)
    trackButtonClicked(TrackedButtonNames.soloViewTab, {
      token: !isOob(token) ? (token as MixpanelTokenFragment) : undefined,
      oobToken: isOob(token) ? (token as MixpanelOobTokenFragment) : undefined,
      project,
      customProperties: {
        tab
      }
    })
  }

  const visibleTabs = []
  if (project.artist_display_notes) {
    visibleTabs.push(DisplayNotesView.Notes)
  }
  if (features.length > 0) {
    visibleTabs.push(DisplayNotesView.Features)
  }

  return (
    <motion.div
      key={`${token?.token_id}-notes-${entryDirection}`}
      layout
      initial={{
        opacity: 0,
        x: entryDirection === 'left' ? -300 : 0,
        y: entryDirection === 'bottom' ? 300 : 0
      }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={{
        opacity: 0,
        x: entryDirection === 'left' ? -300 : 0,
        y: entryDirection === 'bottom' ? 300 : 0
      }}
      transition={{ type: 'tween', duration: 0.3 }}
      className={`z-40 ${className}`}
      onClick={(event) => {
        event.stopPropagation()
      }}
    >
      <div className="border-outline-inverted relative flex h-full flex-col justify-between border bg-transparent">
        <div>
          <div className="p-xs absolute right-0 top-0">
            <button
              onClick={() => {
                onClose()
              }}
            >
              <CloseDrawerIcon className="fill-on-surface-variant w-sm h-sm -rotate-90 md:rotate-0" />
            </button>
          </div>
          <div className="px-md text-on-surface-inverted gap-y-xs pt-md md:pt-xl flex flex-col">
            <HeadlineText className="">
              {`${project.name} ${isOob(token) ? 'sample' : `#${getInvocationFromTokenId(token.token_id)}`}`}
            </HeadlineText>
            <BodyText>{`${project.artist_name}`}</BodyText>
            <div className="text-on-surface-inverted-variant gap-x-xs flex">
              {'owner_address' in token ? (
                <AllPurposeLink
                  href={userProfileRoute(token.owner?.profile_by_id?.username ?? token.owner_address)}
                  className="gap-x-2xs flex"
                >
                  <DetailText className="hover:underline">{`Owned by ${ownerDisplayName}`}</DetailText>
                  {!token.list_price ? (
                    <ArrowSquareOutIcon className="w-sm h-sm fill-on-surface-inverted-variant" />
                  ) : null}
                </AllPurposeLink>
              ) : null}
              {'list_price' in token && token.list_price ? (
                <AllPurposeLink
                  href={marketplaceTokenRoute(token.contract_address, token.token_id)}
                  className="gap-x-2xs flex"
                >
                  <DetailText className="font-medium hover:underline">LISTED FOR SALE</DetailText>
                  <ArrowSquareOutIcon className="w-sm h-sm fill-on-surface-inverted-variant" />
                </AllPurposeLink>
              ) : null}
            </div>
            {isOob(token) ? (
              <div className="p-sm border-outline-inverted border">
                <BodyText>
                  The displayed output is a live-generated sample and not a final artwork in the collection. Continue
                  generating samples to explore the range of the artist&apos;s algorithm.
                </BodyText>
              </div>
            ) : null}
          </div>
          {visibleTabs.length > 0 ? (
            <div className="gap-x-md border-outline-inverted px-md pt-md flex w-full items-end justify-start border-b">
              {visibleTabs.map((tab) => (
                <button
                  className={`p-2xs flex flex-col items-center rounded-none ${
                    notesView === tab ? 'border-on-surface-inverted border-b-2 bg-transparent' : ''
                  }`}
                  onClick={() => changeTabs(tab)}
                  key={tab}
                >
                  <BodyText className={`${notesView === tab ? 'text-on-surface-inverted' : 'text-on-surface-variant'}`}>
                    {tab}
                  </BodyText>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="px-md py-sm scrollbar-hidden relative w-full grow self-start overflow-y-scroll">
          {notesView === DisplayNotesView.Notes ? (
            <BodyText className="text-on-surface-inverted scrollbar-hidden">{project.artist_display_notes}</BodyText>
          ) : (
            <div className="gap-y-md flex w-full flex-col">
              {features.map(([featureName, featureValue]) => (
                <div key={featureName} className="gap-y-2xs flex flex-col break-words">
                  <DetailText className="text-on-surface-inverted-variant">{featureName}</DetailText>
                  <BodyText className="text-on-surface-inverted">{`${featureValue}`}</BodyText>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="gap-xs p-md border-outline-inverted flex w-full items-center justify-center border-t md:flex-col xl:flex-row">
          {token.media_url ? (
            <AllPurposeLink
              href={token.media_url ?? ''}
              className={cn(buttonVariants({ colorTheme: 'inverted-secondary' }), 'gap-x-xs w-full')}
              onClick={() => {
                trackButtonClicked(TrackedButtonNames.viewImage, {
                  token: !isOob(token) ? (token as MixpanelTokenFragment) : undefined,
                  oobToken: isOob(token) ? (token as MixpanelOobTokenFragment) : undefined,
                  project
                })
              }}
            >
              <ImageIcon className="fill-on-primary-inverted-alpha" />
              <span className="text-inherit">View image</span>
            </AllPurposeLink>
          ) : null}
          <Button
            colorTheme="inverted-secondary"
            onClick={() => {
              trackButtonClicked(TrackedButtonNames.fullScreen, {
                token: !isOob(token) ? (token as MixpanelTokenFragment) : undefined,
                oobToken: isOob(token) ? (token as MixpanelOobTokenFragment) : undefined,
                project
              })
              onFullScreenClick()
            }}
            className="w-full"
            iconLeft={<FullScreenIcon className="fill-on-primary-inverted-alpha" />}
          >
            Full screen
          </Button>
        </div>
      </div>
    </motion.div>
  )
}
